import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { Locale } from "@/Common/Models/Localization/Locale";
import { ObstetricalFileControlRenderer } from "@/Patient/Components/MedicalNote/ObstetricalFile/ControlRenderer/ObstetricalFileControlRenderer";

import "./ObstetricalFileResourcesQuestionnaireTable.css";

interface ObstetricalFileResourcesQuestionnaireTableProps {
    title: string;
    subTitle: string;
    subTitleClassName?: string;
    extraSubTitle?: React.ReactNode;
    readonly: boolean;
    date: Date;
    onDateChange: (date: Date) => void;
    id: string;
    columns: { label?: string, pointValue: number }[];
    columnClassName?: string;
    questions: { questionLabel: string, onClick: (value: number) => void, value: number, alternateAnswerLabels?: { [index: number]: string } }[];
    totalScore: number;
    scoreDescription: { titleLabel: string; descriptionLabel?: string };
    hideHeader?: boolean;
    normalTextSubtitle?: boolean;
}

class ObstetricalFileResourcesQuestionnaireTable extends BaseReactComponent<ObstetricalFileResourcesQuestionnaireTableProps, null> {
    private locale = Locale.en;
    private controlRenderer: ObstetricalFileControlRenderer;

    public componentWillMount(): void {
        this.controlRenderer = new ObstetricalFileControlRenderer({ readonly: this.props.readonly, locale: this.locale });
    }

    public componentWillReceiveProps(nextProps: ObstetricalFileResourcesQuestionnaireTableProps): void {
        if (this.props.readonly !== nextProps.readonly)
            this.controlRenderer = new ObstetricalFileControlRenderer({ readonly: nextProps.readonly, locale: this.locale });
    }

    public render(): JSX.Element {
        return (
            <div className="ObstetricalFileResourcesQuestionnaireTable">
                <h4 className="text-bold">{cacheStore.GetString(this.props.title, null, this.locale)}</h4>
                <div className="flex-display flex-space-between flex-col-gap-md flex-align-items-center subtitle">
                    <div>
                        <p className={`${this.props.subTitleClassName ?? ""} ${this.props.normalTextSubtitle ? "" : "f16px"}`}>
                            {cacheStore.GetString(this.props.subTitle, null, this.locale)}
                        </p>
                        {this.props.extraSubTitle}
                    </div>
                    <div className="flex-display flex-end flex-align-items-center">
                        <span className="margin-right-15px">{cacheStore.GetString("DateLabel", null, this.locale)}</span>
                        <span className="margin-right-15px" style={{ width: this.props.readonly ? "80px" : "120px" }}>
                            {this.controlRenderer.renderDateSelector({
                                value: this.props.date,
                                onChange: date => this.props.onDateChange(date),
                                id: `${this.props.id}-date`,
                                className: "width-100"
                            })}
                        </span>
                        {!this.props.readonly && (
                            <a onClick={() => this.props.onDateChange(new Date())}>
                                <span>
                                    {cacheStore.GetString("TodayLabel", null, this.locale)}
                                </span>
                            </a>
                        )}
                    </div>
                </div>
                <div className={`questionnaire-table ${this.props.hideHeader ? "no-header" : ""}`}>
                    <table>
                        <thead>
                            {!this.props.hideHeader && (
                                <tr>
                                    <th className="left-align">{cacheStore.GetString("ObstetricalFile_Resources_HowOftenAreYouBotheredLabel", null, this.locale)}</th>
                                    {this.props.columns.map(p => <th key={`${this.props.id}Header${p.label}`} className={`resources-test ${this.props.columnClassName ?? ""}`}>{cacheStore.GetString(p.label, null, this.locale)}</th>)}
                                </tr>
                            )}
                        </thead>
                        <tbody>
                            {this.props.questions.map((question, i) => (
                                <tr key={`${this.props.id}question${question.questionLabel}`}>
                                    <td>
                                        <div className="question-label">
                                            {`${i + 1}. ${cacheStore.GetString(question.questionLabel, null, this.locale)}`}
                                        </div>
                                    </td>
                                    {this.props.columns.map(headerColumn => (
                                        <td
                                            className={`resources-test ${!this.props.readonly ? "option" : ""} ${this.props.columnClassName ?? ""} ${question.value === headerColumn.pointValue ? "selected" : ""}`}
                                            key={`${this.props.id}question${question.questionLabel}${headerColumn.label}`}
                                            onClick={() => !this.props.readonly && question.onClick(headerColumn.pointValue)}
                                        >
                                            {cacheStore.GetString(question.alternateAnswerLabels?.[headerColumn.pointValue] ?? headerColumn.pointValue.toString(), null, this.locale)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                            <tr>
                                <td colSpan={this.props.columns.length - 1} className="total-score-description">
                                    <span>
                                        <p className="text-bold">{cacheStore.GetString(this.props.scoreDescription.titleLabel, null, this.locale)}</p>
                                        <p>{this.props.scoreDescription.descriptionLabel && cacheStore.GetString(this.props.scoreDescription.descriptionLabel, null, this.locale)}</p>
                                    </span>
                                </td>
                                <td colSpan={2} style={{ textAlign: "center" }}>
                                    <span className="text-bold margin-right-15px">{`${cacheStore.GetString("TotalScoreLabel", null, this.locale)}`}</span>
                                    <span className="text-bold">{this.props.totalScore ?? ""}</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }
}

export { ObstetricalFileResourcesQuestionnaireTable };
